// Plattform-Admins (Systemverwaltung): Liste (Name, E-Mail, 2FA, letzte Anmeldung, aktiv), Admin einladen (Link per E-Mail
// und zum Kopieren), bestehenden Benutzer ernennen, Rechte entziehen, offene Einladungen zurückziehen.
// Heikle Aktionen verlangen das eigene Passwort und – falls eingerichtet – einen Code der Authenticator-App.
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client';
import {
  platformAdminApi, platformAdminsKey, platformInviteLink, usePlatformAdmins, type InviteResult, type PlatformAdmin, type StepUp,
} from '../../api/platform-admins';
import { can, useMe } from '../../api/queries';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import { Confirm, Dialog, Field, formatDateTime, PageHead, relativeTime, Spinner } from '../../components/ui';
import { localeOf } from '../../i18n';

export function PlatformAdminsPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data, isLoading } = usePlatformAdmins();
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<'invite' | 'grant' | null>(null);
  const [revoke, setRevoke] = useState<PlatformAdmin | null>(null);
  const [withdraw, setWithdraw] = useState<{ id: string; email: string } | null>(null);
  const selfMfa = !!data?.items.find((a) => a.isSelf)?.mfaActive;
  const activeCount = (data?.items ?? []).filter((a) => a.status === 'active').length;
  const refresh = () => qc.invalidateQueries({ queryKey: platformAdminsKey });

  return (
    <>
      <PageHead title={t('platformAdmins.title')} sub={t('platformAdmins.sub')}
        actions={<>
          <button className="btn" onClick={() => setDialog('grant')}><Icon name="users" />{t('platformAdmins.grant')}</button>
          <button className="btn primary" onClick={() => setDialog('invite')}><Icon name="plus" />{t('platformAdmins.invite')}</button>
        </>} />
      {isLoading || !data ? <Spinner /> : (
        <div className="col" style={{ gap: 16 }}>
          {!data.mfaRequired && (
            <div className="hint" role="status">
              {t('platformAdmins.mfaHint')}{' '}
              {can(me, 'system.settings') && <Link to="/platform/settings">{t('platformAdmins.mfaHintLink')}</Link>}
            </div>
          )}
          <div className="card scroll" style={{ padding: 10 }}>
            <table className="list" data-testid="platform-admins">
              <thead><tr><th>{t('common.name')}</th><th>{t('platformAdmins.mfa')}</th><th>{t('users.lastLogin')}</th><th>{t('platformAdmins.active')}</th><th /></tr></thead>
              <tbody>
                {data.items.map((a) => {
                  const last = a.status === 'active' && activeCount <= 1;
                  return (
                    <tr key={a.id}>
                      <td><b>{a.displayName}</b>{a.isSelf && <span className="muted small"> ({t('users.you')})</span>}<div className="muted small">{a.email}</div></td>
                      <td>{a.mfaActive ? <span className="pill ok plain">{t('platformAdmins.on')}</span> : <span className="muted">{t('platformAdmins.off')}</span>}</td>
                      <td className="muted" title={a.lastLoginAt ? formatDateTime(a.lastLoginAt, locale) : undefined}>{a.lastLoginAt ? relativeTime(a.lastLoginAt, locale) : '–'}</td>
                      <td>{a.status === 'active'
                        ? <span className="pill ok">{t('common.yes')}</span>
                        : <span className={`pill ${a.status === 'invited' ? 'info' : 'error'}`}>{t(`users.st.${a.status}`)}</span>}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!a.isSelf && (
                          <button className="btn sm ghost danger" disabled={last} title={last ? t('platformAdmins.lastAdmin') : undefined} onClick={() => setRevoke(a)}>
                            <Icon name="x" />{t('platformAdmins.revoke')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card col">
            <h2 style={{ margin: 0 }}>{t('platformAdmins.openInvitations')}</h2>
            {data.invitations.length === 0 ? <div className="muted">{t('platformAdmins.noInvitations')}</div> : (
              <table className="list" data-testid="platform-invitations">
                <thead><tr><th>{t('common.name')}</th><th>{t('platformAdmins.invitedBy')}</th><th>{t('platformAdmins.validUntil')}</th><th /></tr></thead>
                <tbody>
                  {data.invitations.map((i) => (
                    <tr key={i.id}>
                      <td><b>{i.displayName}</b><div className="muted small">{i.email}</div></td>
                      <td className="muted">{i.createdBy ?? '–'}</td>
                      <td className="muted">{formatDateTime(i.expiresAt, locale)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn sm ghost" onClick={() => setWithdraw({ id: i.id, email: i.email })}><Icon name="trash" />{t('platformAdmins.withdraw')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="hint">{t('platformAdmins.rulesHint')}</div>
        </div>
      )}
      {dialog === 'invite' && <InviteAdminDialog mfa={selfMfa} smtp={!!data?.smtpConfigured} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'grant' && <GrantAdminDialog mfa={selfMfa} onClose={() => setDialog(null)} onDone={refresh} />}
      {revoke && <RevokeAdminDialog admin={revoke} mfa={selfMfa} onClose={() => setRevoke(null)} onDone={refresh} />}
      {withdraw && (
        <Confirm danger title={t('platformAdmins.withdraw')} text={t('platformAdmins.withdrawText', { email: withdraw.email })} confirmLabel={t('platformAdmins.withdraw')}
          onClose={() => setWithdraw(null)}
          onConfirm={async () => {
            try {
              await platformAdminApi.withdraw(withdraw.id);
              toast.show(t('platformAdmins.withdrawn'));
              await refresh();
            } catch (e) {
              toast.error(e);
            }
            setWithdraw(null);
          }} />
      )}
    </>
  );
}

type FieldErrors = Partial<Record<'password' | 'code' | 'email' | 'other', string>>;

/** Fehler der erneuten Bestätigung den Feldern zuordnen. */
function useStepUpErrors() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<FieldErrors>({});
  const handle = (e: unknown): boolean => {
    if (e instanceof ApiError && e.status === 422 && e.problem.errors) {
      const f = e.problem.errors;
      if (f.password) setErrors({ password: /Fehlversuche/.test(e.message) ? t('platformAdmins.stepUp.locked') : t('platformAdmins.stepUp.wrongPassword') });
      else if (f.code) setErrors({ code: t('platformAdmins.stepUp.wrongCode') });
      else if (f.email) setErrors({ email: t('platformAdmins.emailInvalid') });
      else return false;
      return true;
    }
    if (e instanceof ApiError && (e.status === 409 || e.status === 404)) {
      setErrors({ other: e.status === 404 ? t('platformAdmins.notFound') : e.message });
      return true;
    }
    return false;
  };
  return { errors, setErrors, handle };
}

function StepUpFields({ mfa, value, onChange, errors }: { mfa: boolean; value: StepUp; onChange: (v: StepUp) => void; errors: FieldErrors }) {
  const { t } = useTranslation();
  return (
    <div className="col" style={{ gap: 8 }} data-testid="step-up">
      <div className="hint">{mfa ? t('platformAdmins.stepUp.hintMfa') : t('platformAdmins.stepUp.hint')}</div>
      <Field label={t('platformAdmins.stepUp.password')} error={errors.password}>
        <input type="password" required autoComplete="current-password" value={value.password} onChange={(e) => onChange({ ...value, password: e.target.value })} />
      </Field>
      {mfa && (
        <Field label={t('platformAdmins.stepUp.code')} error={errors.code} hint={t('platformAdmins.stepUp.codeHint')}>
          <input inputMode="numeric" autoComplete="one-time-code" value={value.code ?? ''} onChange={(e) => onChange({ ...value, code: e.target.value })} />
        </Field>
      )}
    </div>
  );
}

const stepUpBody = (s: StepUp, mfa: boolean): StepUp => ({ password: s.password, ...(mfa && s.code?.trim() ? { code: s.code.trim() } : {}) });
const stepUpReady = (s: StepUp, mfa: boolean) => !!s.password && (!mfa || !!s.code?.trim());

function DialogForm({ title, onClose, onSubmit, submitLabel, ready, busy, danger, children }: {
  title: string; onClose: () => void; onSubmit: () => void; submitLabel: string; ready: boolean; busy: boolean; danger?: boolean; children: ReactNode;
}) {
  const { t } = useTranslation();
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (ready && !busy) onSubmit();
  };
  return (
    <Dialog title={title} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className={`btn ${danger ? 'danger solid' : 'primary'}`} disabled={!ready || busy} onClick={() => submit()}>{submitLabel}</button>
    </>}>
      <form className="col" onSubmit={submit}>{children}<button type="submit" hidden /></form>
    </Dialog>
  );
}

function InviteAdminDialog({ mfa, smtp, onClose, onDone }: { mfa: boolean; smtp: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [lang, setLang] = useState<'de' | 'en'>(i18n.language === 'en' ? 'en' : 'de');
  const [step, setStep] = useState<StepUp>({ password: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const { errors, setErrors, handle } = useStepUpErrors();
  if (result) return <InviteResultDialog result={result} onClose={onClose} />;
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      setResult(await platformAdminApi.invite({ email: email.trim(), displayName: name.trim(), locale: lang, ...stepUpBody(step, mfa) }));
      await onDone();
    } catch (e) {
      if (!handle(e)) toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DialogForm title={t('platformAdmins.invite')} onClose={onClose} onSubmit={() => void submit()} submitLabel={t('platformAdmins.sendInvite')} busy={busy}
      ready={/.+@.+\..+/.test(email) && !!name.trim() && stepUpReady(step, mfa)}>
      <div className="muted">{smtp ? t('platformAdmins.inviteSub') : t('platformAdmins.inviteSubNoSmtp')}</div>
      <Field label={t('login.email')} error={errors.email}><input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      <Field label={t('common.name')}><input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" /></Field>
      <Field label={t('platformAdmins.mailLanguage')}>
        <select value={lang} onChange={(e) => setLang(e.target.value as 'de' | 'en')}><option value="de">Deutsch</option><option value="en">English</option></select>
      </Field>
      <StepUpFields mfa={mfa} value={step} onChange={(v) => { setStep(v); setErrors({}); }} errors={errors} />
      {errors.other && <div className="error" role="alert">{errors.other}</div>}
    </DialogForm>
  );
}

function InviteResultDialog({ result, onClose }: { result: InviteResult; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const link = platformInviteLink(result.token);
  return (
    <Dialog title={t('platformAdmins.invite')} onClose={onClose} actions={<button className="btn primary" onClick={onClose}>{t('common.close')}</button>}>
      <div className="hint" role="status">
        {result.mailQueued ? t('platformAdmins.mailSent', { email: result.email }) : t('platformAdmins.mailNotSent', { email: result.email })}
      </div>
      <div className="col" style={{ gap: 6 }}>
        <div className="muted small">{t('platformAdmins.linkHint', { when: formatDateTime(result.expiresAt, localeOf(i18n.language)) })}</div>
        <div className="row">
          <input className="grow mono" readOnly value={link} aria-label={t('platformAdmins.link')} onFocus={(e) => e.target.select()} data-testid="admin-invite-link" />
          <button className="btn" onClick={() => void navigator.clipboard?.writeText(link).then(() => toast.show(t('common.copied')))}><Icon name="copy" />{t('common.copy')}</button>
        </div>
      </div>
    </Dialog>
  );
}

function GrantAdminDialog({ mfa, onClose, onDone }: { mfa: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<StepUp>({ password: '' });
  const [busy, setBusy] = useState(false);
  const { errors, setErrors, handle } = useStepUpErrors();
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      const u = await platformAdminApi.grant({ email: email.trim(), ...stepUpBody(step, mfa) });
      toast.show(t('platformAdmins.granted', { name: u.displayName }));
      await onDone();
      onClose();
    } catch (e) {
      if (!handle(e)) toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DialogForm title={t('platformAdmins.grant')} onClose={onClose} onSubmit={() => void submit()} submitLabel={t('platformAdmins.grantAction')} busy={busy}
      ready={/.+@.+\..+/.test(email) && stepUpReady(step, mfa)}>
      <div className="muted">{t('platformAdmins.grantSub')}</div>
      <Field label={t('login.email')} error={errors.email}><input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      <StepUpFields mfa={mfa} value={step} onChange={(v) => { setStep(v); setErrors({}); }} errors={errors} />
      {errors.other && <div className="error" role="alert">{errors.other}</div>}
    </DialogForm>
  );
}

function RevokeAdminDialog({ admin, mfa, onClose, onDone }: { admin: PlatformAdmin; mfa: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [step, setStep] = useState<StepUp>({ password: '' });
  const [busy, setBusy] = useState(false);
  const { errors, setErrors, handle } = useStepUpErrors();
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      await platformAdminApi.revoke(admin.id, stepUpBody(step, mfa));
      toast.show(t('platformAdmins.revoked', { name: admin.displayName }));
      await onDone();
      onClose();
    } catch (e) {
      if (!handle(e)) toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DialogForm danger title={t('platformAdmins.revokeTitle', { name: admin.displayName })} onClose={onClose} onSubmit={() => void submit()}
      submitLabel={t('platformAdmins.revoke')} busy={busy} ready={stepUpReady(step, mfa)}>
      <div className="muted">{t('platformAdmins.revokeText', { name: admin.displayName, email: admin.email })}</div>
      <StepUpFields mfa={mfa} value={step} onChange={(v) => { setStep(v); setErrors({}); }} errors={errors} />
      {errors.other && <div className="error" role="alert">{errors.other}</div>}
    </DialogForm>
  );
}
